import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, CheckCircle, XCircle, MessageSquare, ExternalLink, Loader2, AlertTriangle, Car, Hash } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';

const GOOGLE_REVIEW_URL = "https://g.page/r/CRkQgIxh65OFEBM/review";

const SurveyPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  
  // Form state
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [deliveryOk, setDeliveryOk] = useState(null);
  const [professional, setProfessional] = useState(null);
  const [wouldReturn, setWouldReturn] = useState(null);
  const [comment, setComment] = useState('');
  const [reservationCode, setReservationCode] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  
  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const response = await api.get(`/satisfaction/survey/${token}`);
        if (response.data.status === 'COMPLETED') {
          setError('Esta encuesta ya ha sido completada.');
        } else {
          setSurvey(response.data);
        }
      } catch (err) {
        setError('Encuesta no encontrada o enlace inválido.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSurvey();
  }, [token]);
  
  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Por favor, selecciona una valoración');
      return;
    }
    if (deliveryOk === null || professional === null || wouldReturn === null) {
      toast.error('Por favor, responde todas las preguntas');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await api.post(`/satisfaction/survey/${token}/submit`, {
        rating,
        delivery_ok: deliveryOk,
        professional,
        would_return: wouldReturn,
        comment: comment || null,
        reservation_code_manual: reservationCode || null,
        vehicle_plate_manual: vehiclePlate || null
      });
      
      setResult(response.data);
      setSubmitted(true);
      toast.success('¡Gracias por tu valoración!');
    } catch (err) {
      toast.error('Error al enviar la encuesta');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleReviewClick = async () => {
    try {
      await api.post(`/satisfaction/survey/${result.survey_id}/review-click`);
      window.open(GOOGLE_REVIEW_URL, '_blank');
    } catch (err) {
      // Still open Google even if tracking fails
      window.open(GOOGLE_REVIEW_URL, '_blank');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#AAEE18] animate-spin" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="bg-[#1a1a1a] rounded-2xl p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Encuesta no disponible</h1>
          <p className="text-gray-400">{error}</p>
          <Button
            onClick={() => navigate('/')}
            className="mt-6 bg-[#AAEE18] hover:bg-[#94D410] text-black"
          >
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }
  
  if (submitted && result) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#1a1a1a] rounded-2xl p-8 max-w-md w-full text-center"
        >
          {result.show_review ? (
            <>
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">
                ¡Gracias por tu valoración!
              </h1>
              <p className="text-gray-400 mb-6">
                Nos alegramos de que hayas tenido una buena experiencia. 
                Nos ayudarías muchísimo dejando una reseña en Google.
              </p>
              
              <Button
                onClick={handleReviewClick}
                className="w-full bg-[#AAEE18] hover:bg-[#94D410] text-black font-bold py-4 text-lg mb-4"
                data-testid="google-review-btn"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Dejar reseña en Google
              </Button>
              
              <div className="bg-[#0a0a0a] rounded-xl p-4 mt-6">
                <p className="text-sm text-[#AAEE18] font-medium">
                  🎁 Como agradecimiento, recibirás un 9% de descuento para tu próxima reserva en tu email.
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  (Debes reseñar en Google para obtener tu código)
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <MessageSquare className="w-10 h-10 text-blue-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">
                Gracias por tu feedback
              </h1>
              <p className="text-gray-400 mb-6">
                Lo tendremos muy en cuenta para seguir mejorando nuestro servicio.
                Tu opinión es muy importante para nosotros.
              </p>
              
              <Button
                onClick={() => navigate('/')}
                className="w-full bg-[#AAEE18] hover:bg-[#94D410] text-black font-bold py-4"
              >
                Volver al inicio
              </Button>
            </>
          )}
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#0a0a0a] py-8 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/brand/cg-valet-parking.png"
            alt="CG Valet Parking"
            className="h-12 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-white">
            ¿Qué tal fue tu experiencia?
          </h1>
          {survey?.reservation_code && (
            <p className="text-gray-400 mt-2">
              Reserva: <span className="font-mono text-[#AAEE18]">{survey.reservation_code}</span>
            </p>
          )}
        </div>
        
        {/* Survey Form */}
        <div className="bg-[#1a1a1a] rounded-2xl p-6 space-y-8">
          
          {/* Star Rating */}
          <div className="text-center">
            <label className="block text-white font-medium mb-4">
              ¿Cómo valorarías tu experiencia general?
            </label>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`star-${star}`}
                >
                  <Star
                    className={`w-10 h-10 ${
                      star <= (hoverRating || rating)
                        ? 'fill-[#AAEE18] text-[#AAEE18]'
                        : 'text-gray-600'
                    } transition-colors`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-[#AAEE18] mt-2 text-sm">
                {rating === 1 && 'Muy mala'}
                {rating === 2 && 'Mala'}
                {rating === 3 && 'Regular'}
                {rating === 4 && 'Buena'}
                {rating === 5 && 'Excelente'}
              </p>
            )}
          </div>
          
          {/* Yes/No Questions */}
          <div className="space-y-6">
            
            {/* Delivery Question */}
            <div>
              <label className="block text-white font-medium mb-3">
                ¿El proceso de entrega y recogida fue rápido y cómodo?
              </label>
              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={() => setDeliveryOk(true)}
                  variant={deliveryOk === true ? "default" : "outline"}
                  className={`flex-1 py-6 ${
                    deliveryOk === true 
                      ? 'bg-green-600 hover:bg-green-700 border-green-600' 
                      : 'border-gray-600 text-gray-300 hover:border-green-600'
                  }`}
                  data-testid="delivery-yes"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Sí
                </Button>
                <Button
                  type="button"
                  onClick={() => setDeliveryOk(false)}
                  variant={deliveryOk === false ? "default" : "outline"}
                  className={`flex-1 py-6 ${
                    deliveryOk === false 
                      ? 'bg-red-600 hover:bg-red-700 border-red-600' 
                      : 'border-gray-600 text-gray-300 hover:border-red-600'
                  }`}
                  data-testid="delivery-no"
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  No
                </Button>
              </div>
            </div>
            
            {/* Professional Question */}
            <div>
              <label className="block text-white font-medium mb-3">
                ¿El trato recibido fue profesional?
              </label>
              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={() => setProfessional(true)}
                  variant={professional === true ? "default" : "outline"}
                  className={`flex-1 py-6 ${
                    professional === true 
                      ? 'bg-green-600 hover:bg-green-700 border-green-600' 
                      : 'border-gray-600 text-gray-300 hover:border-green-600'
                  }`}
                  data-testid="professional-yes"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Sí
                </Button>
                <Button
                  type="button"
                  onClick={() => setProfessional(false)}
                  variant={professional === false ? "default" : "outline"}
                  className={`flex-1 py-6 ${
                    professional === false 
                      ? 'bg-red-600 hover:bg-red-700 border-red-600' 
                      : 'border-gray-600 text-gray-300 hover:border-red-600'
                  }`}
                  data-testid="professional-no"
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  No
                </Button>
              </div>
            </div>
            
            {/* Would Return Question */}
            <div>
              <label className="block text-white font-medium mb-3">
                ¿Volverías a utilizar nuestro servicio?
              </label>
              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={() => setWouldReturn(true)}
                  variant={wouldReturn === true ? "default" : "outline"}
                  className={`flex-1 py-6 ${
                    wouldReturn === true 
                      ? 'bg-green-600 hover:bg-green-700 border-green-600' 
                      : 'border-gray-600 text-gray-300 hover:border-green-600'
                  }`}
                  data-testid="return-yes"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Sí
                </Button>
                <Button
                  type="button"
                  onClick={() => setWouldReturn(false)}
                  variant={wouldReturn === false ? "default" : "outline"}
                  className={`flex-1 py-6 ${
                    wouldReturn === false 
                      ? 'bg-red-600 hover:bg-red-700 border-red-600' 
                      : 'border-gray-600 text-gray-300 hover:border-red-600'
                  }`}
                  data-testid="return-no"
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  No
                </Button>
              </div>
            </div>
          </div>
          
          {/* Identification Fields (if not coming from email link) */}
          {!survey?.reservation_code && (
            <div className="space-y-4 border-t border-gray-700 pt-6">
              <p className="text-sm text-gray-400 mb-4">
                Si tienes una queja o reclamación, por favor indica tu reserva:
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white text-sm mb-2 block">
                    <Hash className="w-4 h-4 inline mr-1" />
                    Nº Reserva
                  </Label>
                  <Input
                    value={reservationCode}
                    onChange={(e) => setReservationCode(e.target.value.toUpperCase())}
                    placeholder="PLE12345"
                    className="bg-[#0a0a0a] border-gray-700 text-white placeholder:text-gray-500"
                    data-testid="reservation-code-input"
                  />
                </div>
                <div>
                  <Label className="text-white text-sm mb-2 block">
                    <Car className="w-4 h-4 inline mr-1" />
                    Matrícula
                  </Label>
                  <Input
                    value={vehiclePlate}
                    onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())}
                    placeholder="1234 ABC"
                    className="bg-[#0a0a0a] border-gray-700 text-white placeholder:text-gray-500"
                    data-testid="vehicle-plate-input"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Optional Comment */}
          <div>
            <label className="block text-white font-medium mb-3">
              ¿Quieres contarnos algo más? <span className="text-gray-500 font-normal">(opcional)</span>
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tu opinión nos ayuda a mejorar... Si tienes alguna queja o sugerencia, cuéntanos aquí."
              className="bg-[#0a0a0a] border-gray-700 text-white placeholder:text-gray-500 min-h-[100px]"
              data-testid="comment-input"
            />
          </div>
          
          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-[#AAEE18] hover:bg-[#94D410] text-black font-bold py-6 text-lg"
            data-testid="submit-survey-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              'Enviar valoración'
            )}
          </Button>
        </div>
        
        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Tu opinión es anónima y nos ayuda a mejorar
        </p>
      </motion.div>
    </div>
  );
};

export default SurveyPage;
